// src/components/AiDashboard/AiMyNutritionStatus/AiUser/AiUserFirebaseConsumptionUploader.js

// Uploads confirmed nutrition logs to Firebase (Firestore) in an idempotent, event-tagged way.
// Wire this into AiUserInput's confirm flow once Firebase is configured.

import { getFirestore, collection, doc, setDoc } from "firebase/firestore";

const DEFAULT_SERVICE_NAME = "nutrition.firebaseUploader";

// --------- Change hash (FNV-1a) ---------

function hashStringFNV1a(input = "") {
  let hashValue = 0x811c9dc5;

  for (let index = 0; index < input.length; index += 1) {
    hashValue ^= input.charCodeAt(index);
    hashValue =
      (hashValue +
        ((hashValue << 1) +
          (hashValue << 4) +
          (hashValue << 7) +
          (hashValue << 8) +
          (hashValue << 24))) >>> 0;
  }

  return (`0000000${hashValue.toString(16)}`).slice(-8);
}

// --------- Idempotency cache (TTL ≥ replay window) ---------

const IDEMPOTENCY_TTL_MILLISECONDS = 30 * 60 * 1000;
const idempotencyCache = new Map();

function tryStartIdempotent(idempotencyKey) {
  if (!idempotencyKey) return false;

  const now = Date.now();
  const existingExpiration = idempotencyCache.get(idempotencyKey);

  if (existingExpiration && existingExpiration > now) {
    return false;
  }

  idempotencyCache.set(
    idempotencyKey,
    now + IDEMPOTENCY_TTL_MILLISECONDS
  );

  return true;
}

function markCompleteIdempotent(idempotencyKey) {
  if (!idempotencyKey) return;

  // In a real system we might move this to a durable store;
  // here we simply keep the entry until TTL naturally expires.
}

// --------- Simple retry with exponential backoff + jitter ---------

async function executeWithRetry(operation, options = {}) {
  const maximumAttempts =
    typeof options.maximumAttempts === "number"
      ? options.maximumAttempts
      : 3;
  const baseDelayMilliseconds =
    typeof options.baseDelayMilliseconds === "number"
      ? options.baseDelayMilliseconds
      : 250;
  const maximumJitterMilliseconds =
    typeof options.maximumJitterMilliseconds === "number"
      ? options.maximumJitterMilliseconds
      : 150;

  let lastError = null;

  for (let attemptIndex = 0; attemptIndex < maximumAttempts; attemptIndex += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await operation();
    } catch (error) {
      lastError = error;

      if (attemptIndex === maximumAttempts - 1) {
        break;
      }

      const exponentialDelay =
        baseDelayMilliseconds * Math.pow(2, attemptIndex);
      const jitter =
        Math.random() * maximumJitterMilliseconds -
        maximumJitterMilliseconds / 2;
      const totalDelay = Math.max(0, exponentialDelay + jitter);

      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, totalDelay));
    }
  }

  throw lastError;
}

// --------- Optional event publisher (never-loop guardrails) ---------

function publishUploaderEvent({
  serviceName = DEFAULT_SERVICE_NAME,
  eventType,
  payload,
  ingress = "firebase_uploader",
  allowedEgressServices = ["analytics"],
}) {
  if (typeof window === "undefined" || typeof CustomEvent === "undefined") {
    return;
  }

  if (!payload || !eventType) {
    return;
  }

  const visitedIncoming = Array.isArray(payload.visited)
    ? payload.visited
    : [];

  // Drop self/seen events
  if (
    payload.source === serviceName ||
    visitedIncoming.includes(serviceName)
  ) {
    return;
  }

  // Require entity identifier
  if (!payload.entityId) {
    return;
  }

  // Change detection + idempotency key
  const changeHash = hashStringFNV1a(JSON.stringify(payload || {}));
  const idempotencyKey = `${serviceName}:${eventType}:${changeHash}`;

  if (!tryStartIdempotent(idempotencyKey)) {
    // Duplicate in replay window
    return;
  }

  const envelope = {
    type: eventType,
    source: serviceName,
    requestId:
      payload.requestId ||
      (typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function" &&
        crypto.randomUUID()) ||
      `${Date.now()}-${Math.random()}`,
    traceId:
      payload.traceId ||
      (typeof crypto !== "undefined" &&
        typeof crypto.randomUUID === "function" &&
        crypto.randomUUID()) ||
      `${Date.now()}-${Math.random()}`,
    visited: [...visitedIncoming, serviceName],
    hopCount: (payload.hopCount || 0) + 1,
    publishedAt: new Date().toISOString(),
    idempotencyKey,
    changeHash,
    ingress,
    allowEgress: allowedEgressServices,
    payload,
  };

  // Orchestration guardrail: max hop count
  if (envelope.hopCount > 10) {
    // In a real system this would go to a DLQ instead of being dispatched.
    return;
  }

  try {
    const event = new CustomEvent("phn:event", { detail: envelope });
    window.dispatchEvent(event);
    markCompleteIdempotent(idempotencyKey);
  } catch {
    // In a real system this failure would go to DLQ or error monitoring.
  }
}

// --------- Main uploader class ---------

export default class AiUserFirebaseConsumptionUploader {
  /**
   * Uploads a confirmed consumption log to Firestore.
   * The write is idempotent based on the full payload (user, servings, items, nutritionJsonSnapshot).
   */
  static async uploadConsumptionLog({
    userId,
    entityId,
    servings,
    items,
    nutritionJsonSnapshot,
    firestoreInstance,
    serviceName = DEFAULT_SERVICE_NAME,
    metadata = {},
  }) {
    if (!userId) {
      throw new Error(
        "AiUserFirebaseConsumptionUploader.uploadConsumptionLog: userId is required."
      );
    }

    if (!entityId) {
      throw new Error(
        "AiUserFirebaseConsumptionUploader.uploadConsumptionLog: entityId is required."
      );
    }

    const firestore = firestoreInstance || getFirestore();

    const payloadForHash = {
      userId,
      entityId,
      servings: servings || {},
      items: items || [],
      nutritionJsonSnapshot: nutritionJsonSnapshot || null,
    };

    const changeHash = hashStringFNV1a(
      JSON.stringify(payloadForHash)
    );
    const idempotencyKey = `${serviceName}:${entityId}:${changeHash}`;

    if (!tryStartIdempotent(idempotencyKey)) {
      return {
        outcome: "duplicate",
        idempotencyKey,
        changeHash,
      };
    }

    const hopCount = (metadata.hopCount || 0) + 1;

    if (hopCount > 10) {
      // In a production system this would be routed to a DLQ.
      return {
        outcome: "dropped_max_hops",
        idempotencyKey,
        changeHash,
      };
    }

    // Use Firestore auto-generated ID for document
    const collectionReference = collection(
      firestore,
      "users",
      userId,
      "meals"
    );
    const documentReference = doc(collectionReference);

    // Store only the nutrition JSON snapshot with minimal tracking
    const documentData = {
      ...(nutritionJsonSnapshot || {}),
    };

    publishUploaderEvent({
      serviceName,
      eventType: "firebase.consumption_log.write_start",
      payload: {
        entityId,
        userId,
        changeHash,
        hopCount,
      },
      ingress: "firebase_uploader",
    });

    try {
      await executeWithRetry(async () => {
        await setDoc(documentReference, documentData, {
          merge: true,
        });
      });

      markCompleteIdempotent(idempotencyKey);

      publishUploaderEvent({
        serviceName,
        eventType: "firebase.consumption_log.write_success",
        payload: {
          entityId,
          userId,
          changeHash,
          hopCount,
        },
        ingress: "firebase_uploader",
      });

      return {
        outcome: "success",
        idempotencyKey,
        changeHash,
        documentId: documentReference.id,
      };
    } catch (error) {
      publishUploaderEvent({
        serviceName,
        eventType: "firebase.consumption_log.write_error",
        payload: {
          entityId,
          userId,
          changeHash,
          hopCount,
          message: String(error?.message || "unknown_error"),
        },
        ingress: "firebase_uploader",
      });

      throw error;
    }
  }
}
